'use client';

import type { InputHTMLAttributes } from 'react';
import { Search } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from '@/libs/tailwind';
import { CancelCircleIcon } from './icons/CancelCircleIcon';
import { Input } from './Input';

type SearchInputProps = {
  searchQuery: string;
  onSearch: (value: string) => void;
  size?: 'sm' | 'md';
  placeholder?: string;
  onClear?: () => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'size'>;

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ searchQuery, disabled, onSearch, onClear, className = '', size = 'md', ...props }, ref) => {
    const handleClear = () => {
      onSearch('');
      onClear?.();
    };

    return (
      <div className="relative w-full">
        {/* Search Icon */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <Search size={size === 'sm' ? 16 : 20} className={cn(disabled ? 'text-fg-tertiary' : 'text-fg-primary')} />
        </div>

        {/* Input Field */}
        <Input
          ref={ref}
          type="search"
          value={searchQuery}
          onChange={e => onSearch(e.target.value)}
          className={cn(searchQuery ? 'pr-10' : '', size === 'sm' && 'h-8 text-body-sm', className)}
          disabled={disabled}
          {...props}
        />

        {/* Clear Button */}
        {searchQuery && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-fg-primary transition-colors z-10"
            aria-label="Clear search"
            disabled={disabled}
          >
            <CancelCircleIcon size={size === 'sm' ? 20 : 24} className={cn(disabled ? 'text-fg-disabled cursor-default' : 'text-fg-primary cursor-pointer')} />
          </button>
        )}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
